import type { FC, ReactNode } from 'react';
import React, { useEffect } from 'react';
import ReactModal from 'react-modal';

import { MODAL_HEIGHT_CSS_VAR } from '../constants/cssVariables';
import type { BodyProps } from './Body';
import { Body } from './Body';
import type { FooterProps } from './Footer';
import { Footer } from './Footer';
import type { HeaderProps } from './Header';
import { Header } from './Header';
import * as styles from '../styles/Modal.styles';

export type ModalChildrenProps = {
  Body: FC<BodyProps>;
  Footer: FC<FooterProps>;
  Header: FC<HeaderProps>;
};

export interface ModalProps {
  appRootSelector?: string;
  children: (props: ModalChildrenProps) => ReactNode;
  contentLabel: string;
  isModalOpen: boolean;
  onModalClose: VoidFunction;
}

const setModalHeight = () =>
  document.documentElement.style.setProperty(MODAL_HEIGHT_CSS_VAR, `${window.innerHeight}px`);

const CLOSE_TIMEOUT_MS = 250;

export const MODAL_CONTENT_CLASS_NAMES: styles.ModalGlobalStylesProps['contentClassNames'] = {
  base: 'ribbons-modal-content',
  afterOpen: 'ribbons-modal-content--afterOpen',
  beforeClose: 'ribbons-modal-content--beforeClose',
};

export const MODAL_OVERLAY_CLASS_NAMES: styles.ModalGlobalStylesProps['overlayClassNames'] = {
  base: 'ribbons-modal-overlay',
  afterOpen: 'ribbons-modal-overlay--afterOpen',
  beforeClose: 'ribbons-modal-overlay--beforeClose',
};

const ModalGlobalStyles = styles.ModalGlobalStyles({
  contentClassNames: MODAL_CONTENT_CLASS_NAMES,
  overlayClassNames: MODAL_OVERLAY_CLASS_NAMES,
  tranistionMS: CLOSE_TIMEOUT_MS,
});

export const Modal = ({
  appRootSelector = '#NOTHSPageWrapper',
  children,
  contentLabel,
  isModalOpen,
  onModalClose,
}: ModalProps) => {
  ReactModal.setAppElement(appRootSelector);

  useEffect(() => {
    setModalHeight();
    window.addEventListener('resize', setModalHeight);

    return () => window.removeEventListener('resize', setModalHeight);
  }, []);

  return (
    <>
      <ModalGlobalStyles />
      <ReactModal
        className={MODAL_CONTENT_CLASS_NAMES}
        closeTimeoutMS={CLOSE_TIMEOUT_MS}
        contentLabel={contentLabel}
        isOpen={isModalOpen}
        onAfterOpen={setModalHeight}
        onRequestClose={onModalClose}
        overlayClassName={MODAL_OVERLAY_CLASS_NAMES}
      >
        <div css={styles.modalWrapper}>{children({ Header, Body, Footer })}</div>
      </ReactModal>
    </>
  );
};
